import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/use-t';
import {
  formatMinor,
  usePayments,
  useReconcilePayment,
  type PaymentAttemptSummary,
  type PaymentState,
  type ReconcileAction,
} from '@/hooks/use-payments';

export function PaymentsReconciliationPage() {
  const t = useT('admin_ui');
  const [filter, setFilter] = useState<'stuck' | 'all' | PaymentState>('stuck');
  const { data, isLoading } = usePayments({
    pageSize: 100,
    stuckOnly: filter === 'stuck',
    state: filter === 'stuck' || filter === 'all' ? undefined : filter,
  });

  const statePills: { label: string; value: 'stuck' | 'all' | PaymentState }[] = [
    { label: t('payments.reconciliation.filter.stuck', 'Stuck (queue)'), value: 'stuck' },
    { label: t('payments.reconciliation.filter.all', 'All'), value: 'all' },
    { label: t('payments.reconciliation.filter.approved', 'Approved'), value: 'approved' },
    { label: t('payments.reconciliation.filter.declined', 'Declined'), value: 'declined' },
    {
      label: t('payments.reconciliation.filter.indeterminate', 'Indeterminate'),
      value: 'indeterminate',
    },
    { label: t('payments.reconciliation.filter.reversed', 'Reversed'), value: 'reversed' },
  ];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">
          {t('payments.reconciliation.title', 'Payment reconciliation')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'payments.reconciliation.description',
            'Stuck attempts (awaiting-response / indeterminate > 2 min) need manager review. Decide: void (no funds moved), approve (vendor batch confirmed), or queue a reversal (funds moved but receipt failed).',
          )}
        </p>
      </header>

      <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {statePills.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setFilter(opt.value)}
            className={
              filter === opt.value
                ? 'rounded bg-primary px-3 py-1 text-primary-foreground'
                : 'rounded px-3 py-1 text-muted-foreground hover:bg-secondary'
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-border bg-card">
        <Table>
          <THead>
            <TR>
              <TH>{t('payments.reconciliation.col.initiated', 'Initiated')}</TH>
              <TH>{t('payments.reconciliation.col.method', 'Method')}</TH>
              <TH className="text-right">{t('payments.reconciliation.col.amount', 'Amount')}</TH>
              <TH>{t('payments.reconciliation.col.state', 'State')}</TH>
              <TH>{t('payments.reconciliation.col.vendorRef', 'Vendor ref')}</TH>
              <TH>{t('payments.reconciliation.col.reason', 'Reason')}</TH>
              <TH className="w-64">{t('payments.reconciliation.col.actions', 'Actions')}</TH>
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={7} className="py-8 text-center text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.attempts.length === 0 && (
              <TR>
                <TD colSpan={7} className="py-8 text-center text-muted-foreground">
                  {filter === 'stuck'
                    ? t('payments.reconciliation.empty.stuck', 'No stuck attempts. Queue is clear.')
                    : t('payments.reconciliation.empty.filtered', 'No matching attempts.')}
                </TD>
              </TR>
            )}
            {data?.attempts.map((p) => (
              <Row key={p.id} attempt={p} />
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function Row({ attempt }: { attempt: PaymentAttemptSummary }) {
  const t = useT('admin_ui');
  // RL-373: an action's error belongs to the attempt state it was raised
  // against. Once the attempt moves on — reconciled by the next action here, or
  // by someone else and picked up on refetch — the message is stale and a
  // manager-review queue must not keep showing it.
  const [error, setError] = useState<{ message: string; state: PaymentState } | null>(null);
  const latestAction = useRef(0);
  const actionInFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const shownError = error !== null && error.state === attempt.state ? error.message : null;
  const beginAction = (): number | null => {
    if (actionInFlight.current) return null;
    actionInFlight.current = true;
    setBusy(true);
    setError(null);
    return ++latestAction.current;
  };
  const finishAction = (action: number, message: string | null): void => {
    if (action !== latestAction.current) return;
    actionInFlight.current = false;
    setBusy(false);
    setError(message === null ? null : { message, state: attempt.state });
  };
  const stuck = attempt.state === 'awaiting_response' || attempt.state === 'indeterminate';

  return (
    <TR>
      <TD className="text-xs">{formatDateTime(attempt.initiatedAt)}</TD>
      <TD>
        <Badge variant="outline">{attempt.method}</Badge>
      </TD>
      <TD className="text-right tabular-nums">
        {formatMinor(attempt.amountMinor, attempt.currencyCode)}
      </TD>
      <TD>
        <StateBadge state={attempt.state} />
      </TD>
      <TD className="text-xs text-muted-foreground">{attempt.vendorAttemptRef ?? '—'}</TD>
      <TD className="text-xs text-muted-foreground">
        {attempt.reasonMessage ?? attempt.vendorResponseCode ?? '—'}
      </TD>
      <TD>
        <div className="flex items-center gap-1">
          {/* RL-329: no "Open" drill-in until an attempt-detail page exists —
              it navigated to an unregistered route and bounced to the
              Dashboard. */}
          {stuck && (
            <>
              <ReconcileButton
                attemptId={attempt.id}
                action="mark_voided"
                label={t('payments.reconciliation.action.void', 'Void')}
                title={t(
                  'payments.reconciliation.action.void.title',
                  'Vendor confirmed no settlement',
                )}
                onStart={beginAction}
                onResult={finishAction}
                busy={busy}
              />
              <ReconcileButton
                attemptId={attempt.id}
                action="mark_approved"
                label={t('payments.reconciliation.action.approve', 'Approve')}
                title={t(
                  'payments.reconciliation.action.approve.title',
                  'Batch reconciliation confirmed settlement',
                )}
                onStart={beginAction}
                onResult={finishAction}
                busy={busy}
              />
              <ReconcileButton
                attemptId={attempt.id}
                action="issue_reversal"
                label={t('payments.reconciliation.action.reverse', 'Reverse')}
                destructive
                title={t(
                  'payments.reconciliation.action.reverse.title',
                  'Funds moved but receipt failed; queue reversal',
                )}
                onStart={beginAction}
                onResult={finishAction}
                busy={busy}
              />
            </>
          )}
        </div>
        {shownError !== null && <p className="mt-1 text-xs text-destructive">{shownError}</p>}
      </TD>
    </TR>
  );
}

function ReconcileButton({
  attemptId,
  action,
  label,
  title,
  destructive,
  onStart,
  onResult,
  busy,
}: {
  attemptId: string;
  action: ReconcileAction;
  label: string;
  title: string;
  destructive?: boolean;
  onStart: () => number | null;
  onResult: (action: number, msg: string | null) => void;
  busy: boolean;
}) {
  const t = useT('admin_ui');
  const reconcile = useReconcilePayment();
  const [reason, setReason] = useState('');

  async function submit(): Promise<void> {
    const pendingAction = onStart();
    if (pendingAction === null) return;
    try {
      await reconcile.mutateAsync({ id: attemptId, action, reason });
      setReason('');
      onResult(pendingAction, null);
    } catch (err) {
      onResult(
        pendingAction,
        extractError(err, t('payments.reconciliation.error.unexpected', 'Unexpected error')),
      );
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant={destructive ? 'outline' : 'ghost'} size="sm" title={title} disabled={busy}>
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}?</AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              'payments.reconciliation.confirm.description',
              'This action is audited. Provide a short reason — auditors and the next manager reviewing the queue rely on it.',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder={t(
            'payments.reconciliation.confirm.reasonPlaceholder',
            'e.g. acquirer confirmed no settlement on attempt',
          )}
        />
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(destructive && buttonVariants({ variant: 'destructive' }))}
            onClick={submit}
            disabled={busy || reason.trim().length === 0}
          >
            {label}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function StateBadge({ state }: { state: PaymentState }) {
  switch (state) {
    case 'approved':
      return <Badge variant="success">approved</Badge>;
    case 'declined':
    case 'failed':
      return <Badge variant="destructive">{state}</Badge>;
    case 'awaiting_response':
    case 'indeterminate':
      return <Badge variant="destructive">{state.replace('_', ' ')}</Badge>;
    case 'voided':
    case 'reversed':
      return <Badge variant="outline">{state}</Badge>;
    default:
      return <Badge variant="outline">{state}</Badge>;
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function extractError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join('; ');
    return err.message;
  }
  return fallback;
}
